import React, { useState, useEffect, useRef } from "react";
import { Menu, User, Grid, LogOut, Briefcase, LayoutDashboard, Mail, Settings } from "lucide-react";
import { Sidebar, TopBar, SidebarHeader, SidebarToggle, MobileToggle, Backdrop, NavigationPanel, NavList, NavItem, NavIcon, LogoContainer, BrandMark, UserInfoSection, UserSummary, UserAvatar, UserDetails, RoleSwitcher, LogoutButton, NotificationBadge } from "./sidebar.styles";
import { useNavigate } from "react-router-dom";
import { logout } from "../../services/utils/processors";
import { SelectButton } from "primereact/selectbutton";
import ThemeToggler from "../theme-toggler";
import { useSetRole } from "../../services/api/users-api";
import { useGetUnreadCount } from "../../services/api/contacts-api";

const ReusableSidebar = ({ 
  isSidebarOpen, 
  handleToggleSidebar, 
  selectedPage, 
  user,
  loading = false,
  customMenuItems = null,
  basePath = '/settings'
}) => {
  const navigate = useNavigate();
  const { mutate: setRole, isPending: roleLoading } = useSetRole();
  const { data: unreadData } = useGetUnreadCount();
  const [selectedRole, setSelectedRole] = useState(user?.role);
  const navigationTimeoutRef = useRef(null);

  const hasUnreadMessages = unreadData?.count > 0;

  // Sync selectedRole with user role when user changes
  useEffect(() => {
    if (user?.role && user.role !== selectedRole) {
      setSelectedRole(user.role);
    }
  }, [user?.role, selectedRole]);

  useEffect(() => () => {
    if (navigationTimeoutRef.current) {
      window.clearTimeout(navigationTimeoutRef.current);
    }
  }, []);

  const roleOptions = [
    { label: 'Recruiter', value: 'recruiter' },
    { label: 'Default', value: 'default' }
  ];

  const handleRoleChange = (e) => {
    setSelectedRole(e.value);
    setRole(e.value, {
      onSuccess: () => {
        // Navigate based on the new role
        if (e.value === "recruiter") {
          navigate('/recruiter/home');
        } else {
          navigate('/default/profile');
        }
      }
    });
  };

  // Default menu items based on role
  const defaultMenuItems = selectedRole === "recruiter"
    ? [
        { key: "dashboard", label: "Dashboard", icon: <Grid size={22} />, path: "/recruiter/home" },
        { key: "profile", label: "Profile", icon: <User size={22} />, path: "/recruiter/profile" },
        { key: "settings", label: "Settings", icon: <Settings size={22} />, path: "/recruiter/settings" },
      ]
    : [
        { key: "dashboard", label: "Dashboard", icon: <LayoutDashboard size={22} />, path: "/default/dashboard" },
        { key: "profile", label: "Profile", icon: <User size={22} />, path: "/default/profile" },
        { key: "edit-profile", label: "Edit Profile", icon: <User size={22} />, path: "/default/edit-profile" },
        { key: "projects", label: "Projects", icon: <Briefcase size={22} />, path: "/default/projects" },
        { key: "messages", label: "Messages", icon: <Mail size={22} />, path: "/default/messages" },
        { key: "templates", label: "Templates", icon: <Grid size={22} />, path: "/default/templates" },
        { key: "settings", label: "Settings", icon: <Settings size={22} />, path: "/default/settings" },
      ];

  // Use custom menu items if provided, otherwise use default
  const menuItems = customMenuItems || defaultMenuItems;
  const displayName = user?.first_name || user?.username || user?.email?.split('@')[0] || "Account";
  const email = user?.email || "Your account";

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigate = (path) => {
    if (!path) {
      alert("Add more pages as needed");
      return;
    }

    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches;

    if (isMobile && isSidebarOpen) {
      handleToggleSidebar();
      navigationTimeoutRef.current = window.setTimeout(() => {
        navigate(path);
      }, 240);
      return;
    }

    navigate(path);
  };

  return (
    <>
      <MobileToggle
        type="button"
        isOpen={isSidebarOpen}
        aria-label="Open navigation menu"
        aria-expanded={isSidebarOpen}
        onClick={handleToggleSidebar}
      >
        <Menu size={22} aria-hidden="true" />
      </MobileToggle>
      <Backdrop
        type="button"
        isOpen={isSidebarOpen}
        aria-label="Close navigation menu"
        onClick={handleToggleSidebar}
      />
      <Sidebar isOpen={isSidebarOpen} aria-label="Primary navigation">
      <TopBar>
        <SidebarHeader isOpen={isSidebarOpen}>
          <LogoContainer isOpen={isSidebarOpen}>
            <BrandMark aria-hidden="true">P</BrandMark>
            <h3>Portify</h3>
          </LogoContainer>
          <SidebarToggle
            type="button"
            aria-label={isSidebarOpen ? "Collapse navigation menu" : "Expand navigation menu"}
            aria-expanded={isSidebarOpen}
            onClick={handleToggleSidebar}
          >
            <Menu size={22} aria-hidden="true" />
          </SidebarToggle>
        </SidebarHeader>
        {loading ? <div>...</div> : (
          <>
            <NavigationPanel>
              <NavList aria-label="Application pages">
                {menuItems.map(item => (
                  <NavItem
                    key={item.key}
                    isOpen={isSidebarOpen}
                    isActive={selectedPage === item.key}
                    onClick={() => handleNavigate(item.path)}
                  >
                    <NavIcon isActive={selectedPage === item.key}>
                      {React.cloneElement(item.icon, { 'aria-hidden': true })}
                    </NavIcon>
                    {isSidebarOpen && <span>{item.label}</span>}
                    {item.key === "messages" && hasUnreadMessages && (
                      <NotificationBadge isOpen={isSidebarOpen}>
                        {unreadData.count}
                      </NotificationBadge>
                    )}
                  </NavItem>
                ))}
              </NavList>
            </NavigationPanel>
          </>
        )}
      </TopBar>
      
      {/* User Info Section at the bottom */}
      <UserInfoSection isOpen={isSidebarOpen}>
        <UserSummary isOpen={isSidebarOpen} data-email={email} tabIndex="0">
          <UserAvatar aria-hidden="true">
            <User size={17} strokeWidth={2.4} />
          </UserAvatar>
          <UserDetails isOpen={isSidebarOpen}>
            <strong>{displayName}</strong>
          </UserDetails>
        </UserSummary>
        {isSidebarOpen && (
          <RoleSwitcher>
            <SelectButton
              value={selectedRole}
              options={roleOptions}
              onChange={handleRoleChange}
              optionLabel="label"
              style={{ width: '100%' }}
              disabled={roleLoading}
            />
          </RoleSwitcher>
        )}
        
        {/* Theme Toggle */}
        <ThemeToggler isOpen={isSidebarOpen} />
        
        <LogoutButton isOpen={isSidebarOpen} onClick={handleLogout}>
          <LogOut size={20} />
          {isSidebarOpen && <span> Logout</span>}
        </LogoutButton>
      </UserInfoSection>
      </Sidebar>
    </>
  );
};

export default ReusableSidebar;
